import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowRight, Lock, Phone, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { updateProfile } from 'firebase/auth';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Checkbox } from '@/shared/ui/checkbox';
import { useToast } from '@/shared/hooks/use-toast';
import { useAuthStore } from '@/features/auth/store';
import { userApi } from '@/features/user/api';
import { AuthLayout } from '../layouts/AuthLayout';
import { AuthFormLayout } from '../layouts/AuthFormLayout';
import { PasswordInput } from '../components/PasswordInput';
import { LoadingButton } from '../components/LoadingButton';

const PASSWORD_TESTS = [
  { key: 'length',    test: (p: string) => p.length >= 8 },
  { key: 'uppercase', test: (p: string) => /[A-Z]/.test(p) },
  { key: 'lowercase', test: (p: string) => /[a-z]/.test(p) },
  { key: 'number',    test: (p: string) => /\d/.test(p) },
  { key: 'special',   test: (p: string) => /[!@#$%^&*()_+\-=[\]{}';":"|,.<>/?]/.test(p) },
] as const;

const CompleteProfilePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const firebaseUser = useAuthStore((s) => s.firebaseUser);
  const backendUser = useAuthStore((s) => s.user);
  const isLoading = useAuthStore((s) => s.isLoading);
  const linkEmailPassword = useAuthStore((s) => s.linkEmailPassword);
  const patchUser = useAuthStore((s) => s.patchUser);

  useEffect(() => {
    if (isLoading) return;
    if (!firebaseUser) { navigate('/register', { replace: true }); return; }
    const hasEmailPassword = firebaseUser.providerData.some((p) => p.providerId === 'password');
    if (hasEmailPassword) navigate('/app', { replace: true });
  }, [firebaseUser, isLoading, navigate]);

  const hasEmail = !!firebaseUser?.email;
  const hasPhone = !!firebaseUser?.phoneNumber;

  const [name, setName] = useState(firebaseUser?.displayName ?? backendUser?.name ?? '');
  const [email, setEmail] = useState(firebaseUser?.email ?? backendUser?.email ?? '');
  const [phone, setPhone] = useState(firebaseUser?.phoneNumber ?? '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');

  // Sync state if firebaseUser arrives after mount
  useEffect(() => {
    const knownName = firebaseUser?.displayName ?? backendUser?.name ?? '';
    if (knownName && !name) setName(knownName);
    const knownEmail = firebaseUser?.email ?? backendUser?.email ?? '';
    if (knownEmail && !email) setEmail(knownEmail);
    if (firebaseUser?.phoneNumber && !phone) setPhone(firebaseUser.phoneNumber);
  }, [firebaseUser, backendUser]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEmailBlur = async () => {
    const trimmed = email.trim();
    if (!trimmed || hasEmail) return;
    const exists = await userApi.checkEmailExists(trimmed);
    setEmailError(exists ? t('auth.completeProfile.errors.emailTaken') : '');
  };

  const handlePhoneBlur = async () => {
    const trimmed = phone.trim();
    if (!trimmed || hasPhone) return;
    const exists = await userApi.checkPhoneExists(trimmed);
    setPhoneError(exists ? t('auth.completeProfile.errors.phoneTaken') : '');
  };

  const allPasswordReqsMet = PASSWORD_TESTS.every((r) => r.test(password));
  const passwordsMatch = password === confirmPassword;
  const emailReady = hasEmail || email.trim().length > 0;

  const canSubmit =
    name.trim() &&
    emailReady &&
    !emailError &&
    !phoneError &&
    allPasswordReqsMet &&
    passwordsMatch &&
    acceptedTerms &&
    !submitting;

  // What's still blocking — shown as a subtle hint under the button
  const blockingReasons: string[] = [];
  if (!name.trim()) blockingReasons.push('Full name required');
  if (!emailReady) blockingReasons.push('Email address required');
  if (!allPasswordReqsMet) blockingReasons.push('Password does not meet requirements');
  if (password && !passwordsMatch) blockingReasons.push('Passwords do not match');
  if (!acceptedTerms) blockingReasons.push('Please accept the terms');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !firebaseUser) return;
    setSubmitting(true);

    const emailToUse = hasEmail ? (firebaseUser.email ?? email) : email.trim();

    try {
      // 1. Update Firebase display name if changed
      if (firebaseUser.displayName !== name.trim()) {
        await updateProfile(firebaseUser, { displayName: name.trim() });
      }

      // 2. Link email/password credential
      try {
        await linkEmailPassword(emailToUse, password);
      } catch (linkErr: unknown) {
        const code = (linkErr as { code?: string })?.code ?? '';
        if (code === 'auth/email-already-in-use') {
          // Email is registered to a completely different Firebase account
          toast({
            title: t('auth.completeProfile.errors.emailInUse'),
            description: t('auth.completeProfile.errors.emailInUseDesc'),
            variant: 'destructive',
          });
          setSubmitting(false);
          return;
        }
        // provider-already-linked / credential-already-in-use → already done, that's fine
        if (code !== 'auth/provider-already-linked' && code !== 'auth/credential-already-in-use') {
          // Non-fatal: profile still saves, but warn the user
          toast({
            title: t('common.error'),
            description: t('auth.completeProfile.errors.linkFailed'),
            variant: 'destructive',
          });
        }
      }

      // 3. Persist name, phone, email to the backend
      const freshToken = await firebaseUser.getIdToken(true);
      await userApi.completeProfile(freshToken, {
        name: name.trim(),
        phone: phone.trim() || undefined,
        email: emailToUse,
      });

      // Patch in-memory store so /app doesn't briefly show stale null values
      patchUser({ name: name.trim(), email: emailToUse });

      navigate('/app', { replace: true });
    } catch {
      toast({
        title: t('common.error'),
        description: t('auth.completeProfile.errors.failed'),
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading || !firebaseUser) return null;

  return (
    <AuthLayout showBranding={false} showLogout>
      <AuthFormLayout
        title={t('auth.completeProfile.title')}
        subtitle={t('auth.completeProfile.subtitle')}
      >
        <form onSubmit={handleSubmit} className="space-y-5">

          {/* Verified identity card — shows what we already know */}
          {(hasPhone || hasEmail) && (
            <div className="rounded-lg border border-success/30 bg-success/5 px-4 py-3 space-y-1.5">
              <p className="text-xs font-medium text-success">{t('auth.completeProfile.linkedAccounts')}</p>
              {hasPhone && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="w-3.5 h-3.5 text-success shrink-0" />
                  <span className="font-medium tabular-nums">{firebaseUser.phoneNumber}</span>
                  <span className="text-xs text-muted-foreground">— {t('auth.completeProfile.verifiedPhone')}</span>
                </div>
              )}
              {hasEmail && (
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-3.5 h-3.5 text-success shrink-0" />
                  <span className="font-medium">{firebaseUser.email}</span>
                  <span className="text-xs text-muted-foreground">— {t('auth.completeProfile.verifiedEmail')}</span>
                </div>
              )}
            </div>
          )}

          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="cp-name">{t('auth.register.fullName')}</Label>
            <Input
              id="cp-name"
              placeholder={t('auth.completeProfile.namePlaceholder')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-12 input-elegant"
              required
              autoFocus={!name}
            />
          </div>

          {/* Email — phone users don't have one yet */}
          {!hasEmail && (
            <div className="space-y-2">
              <Label htmlFor="cp-email">{t('common.email')}</Label>
              <Input
                id="cp-email"
                type="email"
                placeholder={t('auth.completeProfile.emailPlaceholder')}
                value={email}
                onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
                onBlur={handleEmailBlur}
                className={`h-12 input-elegant ${emailError ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                required
                autoFocus={!!name}
              />
              {emailError && <p className="text-xs text-destructive">{emailError}</p>}
            </div>
          )}

          {/* Phone — Google / email users can optionally add one */}
          {!hasPhone && (
            <div className="space-y-2">
              <Label htmlFor="cp-phone">
                {t('auth.completeProfile.phoneLabel')}{' '}
                <span className="text-xs text-muted-foreground">({t('common.optional')})</span>
              </Label>
              <Input
                id="cp-phone"
                type="tel"
                placeholder={t('auth.completeProfile.phonePlaceholder')}
                value={phone}
                onChange={(e) => { setPhone(e.target.value); setPhoneError(''); }}
                onBlur={handlePhoneBlur}
                className={`h-12 input-elegant ${phoneError ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                autoComplete="tel"
              />
              {phoneError
                ? <p className="text-xs text-destructive">{phoneError}</p>
                : <p className="text-xs text-muted-foreground">{t('auth.completeProfile.phoneHint')}</p>
              }
            </div>
          )}

          {/* Password */}
          <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-accent shrink-0" />
              <p className="text-sm font-medium">{t('auth.completeProfile.passwordSection')}</p>
            </div>
            <p className="text-xs text-muted-foreground">{t('auth.completeProfile.passwordHint')}</p>

            <PasswordInput
              id="cp-password"
              label={t('common.password')}
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
              required
            />
            {password && (
              <div className="space-y-1 pt-1">
                {PASSWORD_TESTS.map((req) => {
                  const met = req.test(password);
                  return (
                    <div key={req.key} className="flex items-center gap-2 text-xs">
                      <CheckCircle className={`w-3.5 h-3.5 ${met ? 'text-success' : 'text-muted-foreground/40'}`} />
                      <span className={met ? 'text-success' : 'text-muted-foreground'}>
                        {t(`auth.register.passwordReqs.${req.key}`)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            <PasswordInput
              id="cp-confirm-password"
              label={t('auth.completeProfile.confirmPassword')}
              value={confirmPassword}
              onChange={setConfirmPassword}
              autoComplete="new-password"
              required
            />
            {confirmPassword && !passwordsMatch && (
              <p className="text-xs text-destructive">{t('auth.completeProfile.passwordMismatch')}</p>
            )}
          </div>

          {/* T&C */}
          <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
            <div className="max-h-40 overflow-y-auto pr-1 space-y-2.5 text-xs text-muted-foreground leading-relaxed">
              <p>
                {t('auth.register.terms.agbBefore')}{' '}
                <Link to="/legal/agb" target="_blank" className="text-accent underline font-medium">
                  {t('auth.register.terms.agbLink')}
                </Link>
                {t('auth.register.terms.agbAfter')}
              </p>
              <p>
                {t('auth.register.terms.datenschutzBefore')}{' '}
                <Link to="/legal/datenschutz" target="_blank" className="text-accent underline font-medium">
                  {t('auth.register.terms.datenschutzLink')}
                </Link>{' '}
                {t('auth.register.terms.datenschutzAfter')}
              </p>
              <p>
                {t('auth.register.terms.platformBefore')}{' '}
                <strong className="text-foreground">{t('auth.register.terms.platformStrong')}</strong>{' '}
                {t('auth.register.terms.platformAfter')}
              </p>
              <p>
                {t('auth.register.terms.paymentBefore')}{' '}
                <strong className="text-foreground">{t('auth.register.terms.paymentStrong')}</strong>
                {t('auth.register.terms.paymentAfter')}
              </p>
              <p>
                {t('auth.register.terms.dataAnalysisBefore')}{' '}
                <strong className="text-foreground">{t('auth.register.terms.dataAnalysisStrong')}</strong>{' '}
                {t('auth.register.terms.dataAnalysisMiddle')}{' '}
                <a href="mailto:dishu.restroos@gmail.com" className="text-accent underline">
                  dishu.restroos@gmail.com
                </a>
                {t('auth.register.terms.dataAnalysisAfter')}
              </p>
            </div>
            <div className="flex items-start gap-3 pt-1 border-t border-border">
              <Checkbox
                id="cp-accept-all"
                checked={acceptedTerms}
                onCheckedChange={(v) => setAcceptedTerms(v === true)}
                className="mt-0.5 shrink-0"
              />
              <label htmlFor="cp-accept-all" className="text-xs text-foreground font-medium leading-relaxed cursor-pointer">
                {t('auth.register.terms.acceptAll')}
              </label>
            </div>
          </div>

          <LoadingButton
            type="submit"
            isLoading={submitting}
            loadingText={t('auth.completeProfile.submitting')}
            icon={ArrowRight}
            disabled={!canSubmit}
          >
            {t('auth.completeProfile.submit')}
          </LoadingButton>

          {/* Show what's still needed when the button is disabled */}
          {!canSubmit && !submitting && blockingReasons.length > 0 && (
            <ul className="space-y-0.5 pl-1">
              {blockingReasons.map((reason) => (
                <li key={reason} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span className="w-1 h-1 rounded-full bg-muted-foreground/50 shrink-0" />
                  {reason}
                </li>
              ))}
            </ul>
          )}
        </form>
      </AuthFormLayout>
    </AuthLayout>
  );
};

export default CompleteProfilePage;
